import React, { useState } from 'react';
import { User } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, AlertCircle, Database } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import { useDateRange } from '../contexts/DateRangeContext';
import { 
  formatCurrency,
  getUsersWithCostsFromData,
  getUserPieDataFromData,
  getUserTrendDataFromData,
  getUserTrendDataForTableFromData
} from '../utils/dataProcessor';
import { useCostDataWithFallback } from '../hooks/useCostData';

interface UsersProps {
  onTabChange?: (tab: string) => void;
}

const Users: React.FC<UsersProps> = ({ onTabChange }) => {
  const [showUsersSeparately, setShowUsersSeparately] = useState<boolean>(false);
  const { dateRange } = useDateRange();

  // React Query 훅 사용 - 날짜 범위 변경 시 자동으로 새 데이터 가져오기
  const { data: costData, isLoading, isError, error } = useCostDataWithFallback({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    enabled: Boolean(dateRange.startDate && dateRange.endDate)
  });

  console.log('🔄 Users 컴포넌트 렌더링:', {
    dateRange,
    isLoading,
    isError,
    dataCount: costData?.length || 0,
    error: error?.message || null
  });

  // 새로운 함수들을 사용하여 데이터 처리
  const users = costData ? getUsersWithCostsFromData(costData) : [];
  const totalCost = users.reduce((sum: number, user: User) => sum + user.totalCost, 0);
  const pieData = costData ? getUserPieDataFromData(costData) : [];

  console.log('📊 처리된 사용자 데이터:', {
    usersCount: users.length,
    totalCost,
    pieDataCount: pieData.length,
    sampleUsers: users.slice(0, 3),
    localStorage_users: localStorage.getItem('users'),
    localStorage_groups: localStorage.getItem('groups')
  });

  // 로딩 상태 처리
  if (isLoading) {
    console.log('⏳ 로딩 상태 렌더링');
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">사용자 데이터를 불러오는 중...</p>
          <p className="text-gray-500 text-sm mt-2">
            날짜 범위: {dateRange.startDate} ~ {dateRange.endDate}
          </p>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (isError) {
    console.log('❌ 에러 상태 렌더링:', error);
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">데이터 로드 중 오류가 발생했습니다</p>
          <p className="text-gray-600 text-sm">
            {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다'}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            날짜 범위: {dateRange.startDate} ~ {dateRange.endDate}
          </p>
        </div>
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!costData || costData.length === 0) {
    console.log('📭 데이터 없음 상태 렌더링');
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Database className="h-8 w-8 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">해당 기간에 데이터가 없습니다</p>
          <p className="text-gray-500 text-sm mt-2">다른 날짜 범위를 선택해보세요</p>
          <p className="text-gray-500 text-xs mt-2">
            현재 날짜 범위: {dateRange.startDate} ~ {dateRange.endDate}
          </p>
        </div>
      </div>
    );
  }

  // 사용자가 설정되지 않은 경우
  if (users.length === 0) {
    console.log('👥 사용자 설정 없음 상태 렌더링');
    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ユーザー</h1>
            <p className="text-gray-600 mt-2">ユーザー別に整理されたコスト分析</p>
          </div>
        </div>
        
        {/* Data Info Badge */}
        <div className="flex items-center space-x-2 mb-6">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            📊 {costData.length}개 항목
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            📅 {dateRange.startDate} ~ {dateRange.endDate}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ⚠️ 사용자 설정 없음
          </span>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">ユーザーが設定されていません</h3>
          <p className="text-gray-500 mb-4">設定でユーザーを作成して、コストデータを整理します</p>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 text-sm text-left">
            <p className="font-medium text-gray-700 mb-2">디버깅 정보:</p>
            <p className="text-gray-600">• 총 데이터 항목: {costData.length}개</p>
            <p className="text-gray-600">• 설정된 사용자: {users.length}명</p>
            <p className="text-gray-600">• localStorage 사용자: {localStorage.getItem('users') ? ' 있음' : '없음'}</p>
          </div>
          <button 
            onClick={() => onTabChange && onTabChange('settings')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            設定に移動
          </button>
        </div>
      </div>
    );
  }

  console.log('✅ 정상 UI 렌더링 진행');

  const getUserColors = () => {
    return [
      { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
      { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
      { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
      { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
      { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
      { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
      { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
      { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
    ];
  };

  const colors = getUserColors();

  // 파이 차트용 SVG 경로 생성 함수
  const createPieSlice = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
      "M", cx, cy, 
      "L", start.x, start.y, 
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  let cumulativeAngle = 0;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">ユーザー</h1>
            <p className="text-gray-600">ユーザー別に整理されたコスト分析</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 mb-1">総コスト</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalCost)}</p>
            <p className="text-sm text-gray-500">選択期間</p>
          </div>
        </div>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker />

      {/* Data Info Badge */}
      <div className="flex items-center space-x-2 mb-6">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          📊 {costData.length}개 항목
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          📅 {dateRange.startDate} ~ {dateRange.endDate}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          👥 {users.length}명 사용자
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Users Section with Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">ユーザー</h3>
          
          <div className="flex items-center justify-center mb-4">
            <svg width="160" height="160" viewBox="0 0 160 160" className="transform -rotate-90">
              {pieData.map((item: any, index: number) => {
                const startAngle = cumulativeAngle;
                const endAngle = cumulativeAngle + (item.percentage / 100) * 360;
                cumulativeAngle = endAngle;
                
                return (
                  <path
                    key={item.id}
                    d={createPieSlice(80, 80, 60, startAngle, endAngle)}
                    fill={item.color.stroke}
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              {/* センターの円 */}
              <circle cx="80" cy="80" r="28" fill="white" />
            </svg>
          </div>

          <div className="space-y-2">
            {pieData.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-2.5 h-2.5 rounded-full mr-2"
                    style={{ backgroundColor: item.color.stroke }}
                  ></div>
                  <span className="text-xs font-medium text-gray-900">{item.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold text-gray-900">{formatCurrency(item.totalCost)}</div>
                  <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cost Trends */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">コストトレンド</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">合計</span>
              <button
                onClick={() => setShowUsersSeparately(!showUsersSeparately)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showUsersSeparately ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showUsersSeparately ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">個別</span>
            </div>
          </div>
          
          {(() => {
            const chartData = getUserTrendDataFromData(costData, showUsersSeparately);
            
            if (chartData.length === 0) {
              return (
                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                  選択した期間に、選択したユーザーの利用可能な日次データがありません
                </div>
              );
            }
            
            if (showUsersSeparately) {
              return (
                <div>
                  {/* 凡例 */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    {users.map((user: any, index: number) => (
                      <div key={user.id} className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: colors[index % colors.length].stroke }}
                        ></div>
                        <span className="text-sm text-gray-600">{user.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value, name) => {
                          const user = users.find((u: any) => u.id === name);
                          return [formatCurrency(value as number), user?.name || name];
                        }}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                      />
                      {users.map((user: any, index: number) => (
                        <Line 
                          key={user.id}
                          type="monotone" 
                          dataKey={user.id} 
                          stroke={colors[index % colors.length].stroke} 
                          strokeWidth={2}
                          dot={{ fill: colors[index % colors.length].stroke, strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5, fill: colors[index % colors.length].stroke }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            } else {
              return (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                    />
                    <Line type="monotone" dataKey="totalCost" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              );
            }
          })()}
        </div>
      </div>

      {/* User Details Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">ユーザー別コスト詳細</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ユーザー
                </th>
                <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  総コスト
                </th>
                <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  デイリーコストトレンド
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pieData.map((item: any, index: number) => {
                const trendData = getUserTrendDataForTableFromData(item, costData);
                const maxValue = Math.max(...trendData, 1);
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                          style={{ backgroundColor: item.color.stroke }}
                        ></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.providers.length} provider{item.providers.length !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(item.totalCost)}</div>
                      <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}% of total</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-40 h-12 mr-4">
                          <svg width="160" height="48" viewBox="0 0 160 48" className="overflow-visible">
                            <defs>
                              <linearGradient id={`gradient-${item.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={item.color.stroke} stopOpacity="0.3"/>
                                <stop offset="100%" stopColor={item.color.stroke} stopOpacity="0.1"/>
                              </linearGradient>
                            </defs>
                            
                            {/* Grid lines for better readability */}
                            <defs>
                              <pattern id={`grid-${item.id}`} width="20" height="48" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 48" fill="none" stroke="#f3f4f6" strokeWidth="0.5"/>
                              </pattern>
                            </defs>
                            <rect width="160" height="48" fill={`url(#grid-${item.id})`} />
                            
                            {/* Trend line */}
                            <polyline
                              fill="none"
                              stroke={item.color.stroke}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              points={trendData.map((value, idx) => {
                                const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                                const y = 48 - (value / maxValue) * 36 - 6;
                                return `${x},${y}`;
                              }).join(' ')}
                            />
                            
                            {/* Fill area under the curve */}
                            <polygon
                              fill={`url(#gradient-${item.id})`}
                              points={`${trendData.map((value, idx) => {
                                const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                                const y = 48 - (value / maxValue) * 36 - 6;
                                return `${x},${y}`;
                              }).join(' ')} 160,48 0,48`}
                            />
                            
                            {/* Data points */}
                            {trendData.map((value, idx) => {
                              const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                              const y = 48 - (value / maxValue) * 36 - 6;
                              return (
                                <circle
                                  key={idx}
                                  cx={x}
                                  cy={y}
                                  r="2"
                                  fill={item.color.stroke}
                                  className="opacity-60"
                                />
                              );
                            })}
                          </svg>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Users; 